import React, { useState } from "react"
import { navigate } from "vike/client/router"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import {
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  CogIcon,
} from "@heroicons/react/24/outline"
import { GetModels, DeleteModel } from "./api"
import AddModelDialog from "./AddModelDialog"

export default function LicenseModels() {
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [allModels, setAllModels] = useState([])
  const [addModelDialog, setAddModelDialog] = useState({
    isOpen: false,
    model: null,
  })

  // Handle select all
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item._id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Handle edit model
  const handleEditModel = (model) => {
    setAddModelDialog({
      isOpen: true,
      model: model,
    })
  }


  // Handle delete model
  const handleDeleteModel = (model) => {
    // Check if model is primary
    if (model.primary) {
      showNotification({
        message: `The model "${model.name}" is primary. Remove its primary status before deletion.`,
        className: "bg-admin_warning text-white",
      })
      return
    }

    showPromptDialog({
      title: "Delete Model",
      message: `Are you sure you want to delete "${model.name}"? This won't affect existing licenses created with this model.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          const response = await DeleteModel(model._id)

          if (response.result) {
            showNotification({
              message: "Model deleted successfully",
              className: "bg-admin_primary text-white",
            })
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.message || response.error || "Failed to delete model",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          // Extract error message from different possible sources
          let errorMessage = "An error occurred while deleting model"
          
          // Check if it's an axios error with response data
          if (error.response && error.response.data) {
            const responseData = error.response.data
            errorMessage = responseData.message || responseData.error || error.message
          } else if (error.message) {
            errorMessage = error.message
          }
          
          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle bulk delete
  const handleDeleteSelected = (allItems) => {
    if (selectedItems.length === 0) return

    // Check for primary models in selection
    const selectedModels = allItems.filter(item => selectedItems.includes(item._id))
    const primaryModels = selectedModels.filter(model => model.primary)

    if (primaryModels.length > 0) {
      const primaryNames = primaryModels.map(model => model.name).join(", ")
      showNotification({
        message: `The model${primaryModels.length > 1 ? "s" : ""} "${primaryNames}" ${primaryModels.length > 1 ? "are" : "is"} primary. Remove their primary status before deletion.`,
        className: "bg-admin_warning text-white",
      })
      return
    }

    showPromptDialog({
      title: "Delete Models",
      message: `Are you sure you want to delete ${selectedItems.length} model${
        selectedItems.length === 1 ? "" : "s"
      }? This won't affect existing licenses.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          let successCount = 0
          let errorCount = 0

          for (const modelId of selectedItems) {
            try {
              const response = await DeleteModel(modelId)
              if (response.result) {
                successCount++
              } else {
                errorCount++
                // Show specific error for failed deletions
                const errorMessage = response.message || response.error || "Failed to delete model"
                showNotification({
                  message: errorMessage,
                  className: "bg-admin_error text-white",
                })
              }
            } catch (error) {
              errorCount++
              // Extract error message from different possible sources
              let errorMessage = "An error occurred while deleting model"
              
              // Check if it's an axios error with response data
              if (error.response && error.response.data) {
                const responseData = error.response.data
                errorMessage = responseData.message || responseData.error || error.message
              } else if (error.message) {
                errorMessage = error.message
              }
              
              showNotification({
                message: errorMessage,
                className: "bg-admin_error text-whtie",
              })
            }
          }

          if (successCount > 0) {
            showNotification({
              message: `${successCount} model${
                successCount === 1 ? "" : "s"
              } deleted successfully${
                errorCount > 0 ? `, ${errorCount} failed` : ""
              }`,
              className: "bg-admin_primary text-white",
            })
          }

          setSelectedItems([])
          setRefreshKey((prev) => prev + 1)
        } catch (error) {
          // Extract error message from different possible sources
          let errorMessage = "An error occurred while deleting models"
          
          // Check if it's an axios error with response data
          if (error.response && error.response.data) {
            const responseData = error.response.data
            errorMessage = responseData.message || responseData.error || error.message
          } else if (error.message) {
            errorMessage = error.message
          }
          
          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Format validity display
  const formatValidity = (days) => {
    if (!days || days === 0) return "Lifetime"
    if (days === 1) return "1 day"
    if (days < 30) return `${days} days`
    if (days < 365) return `${Math.round(days / 30)} months`
    return `${Math.round(days / 365)} year${
      Math.round(days / 365) > 1 ? "s" : ""
    }`
  }

  // Generate sample code based on pattern
  const generateSampleCode = (item) => {
    if (item.explicitCode) return "Custom"
    
    const pattern = item.codePattern || "XXXX-XXXX"
    const codeSet = item.codeSet || "A-Z,0-9"

    // Parse character set
    let chars = ""
    const sets = codeSet.split(",")
    sets.forEach((set) => {
      if (set.trim() === "A-Z") chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
      else if (set.trim() === "0-9") chars += "0123456789"
      else if (set.trim() === "a-z") chars += "abcdefghijklmnopqrstuvwxyz"
    })

    if (!chars) return "XXXX-XXXX"

    return pattern.replace(/X/g, () => {
      return chars.charAt(Math.floor(Math.random() * chars.length))
    })
  }

  // Style overrides for DataTable
  const styleOverrides = {
    table: "w-full table-fixed",
    header: "w-full h-4 flex justify-between items-end gap-2 mt-6 mb-4",
    searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    getRowStyle: (_, item) => {
      if (item && selectedItems.includes(item._id)) {
        return "bg-admin_canvas"
      }
    },
  }

  // Column definitions
  const columns = [
    {
      id: "name",
      title: "Model Name",
      headerClassName: "w-[40%] xl:w-1/4",
      render: (_, item) => (
        <div onClick={() => handleEditModel(item)}>
          <div className="flex items-center gap-2">
            <Typography 
              variant="admin_label" 
              className="whitespace-nowrap cursor-pointer hover:text-admin_primary transition-colors my-0"
            >
              {item.name}
            </Typography>

            {/* Show external badge if external, otherwise show primary badge if primary */}
            {item.external ? (
              <Typography 
                variant="admin_txt2"
                className="text-white font-medium bg-violet-600 rounded-full px-3 py-1"
              >
                external
              </Typography>
            ) : (
              item.primary && (
                <Typography 
                  variant="admin_txt2"
                  className="text-admin_text_inv font-medium bg-admin_progress rounded-full px-3 py-1"
                >
                  primary
                </Typography>
              )
            )}
          </div>
        </div>
      ),
    },
    {
      id: "codePattern",
      title: "Code Pattern",
      headerClassName: "w-[200px]",
      render: (_, item) => (
        <Typography
          variant="admin_txt1"
          className="text-admin_text font-mono bg-admin_neutral_1/50 rounded px-2 py-1"
        >
          {item.explicitCode ? "Custom" : item.codePattern}
        </Typography>
      ),
    },
    {
      id: "sampleCode",
      title: "Sample Code",
      headerClassName: "w-[180px]",
      render: (_, item) => (
        <Typography
          variant="admin_txt1"
          className="font-mono text-admin_primary"
        >
          {generateSampleCode(item)}
        </Typography>
      ),
    },
    {
      id: "seats",
      title: "Seats",
      headerClassName: "w-[60px] text-center",
      render: (_, item) => (
        <Typography 
          variant="admin_body" 
          className="text-center my-0"
        >
          {item.seats}
        </Typography>
      ),
    },
    {
      id: "validity",
      title: "Validity",
      headerClassName: "w-[80px]",
      render: (_, item) => (
        <Typography 
          variant="admin_body_dim"
        >
          {formatValidity(item.validity)}
        </Typography>
      ),
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[90px] xl:w-[120px] text-center",
      render: (_, item) => (
        <div className="flex justify-center gap-1">
          <Button
            variant="admin_icon"
            className="text-admin_text_secondary px-2"
            onClick={() => handleEditModel(item)}
            title="Edit model"
          >
            <PencilIcon className="w-5 h-5" />
          </Button>

          <Button
            variant="admin_icon"
            className="text-admin_text_secondary hover:!text-admin_error px-2"
            onClick={() => handleDeleteModel(item)}
            title="Delete model"
          >
            <TrashIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          License Models
        </Typography>

        <Button
          variant="admin_primary"
          className="whitespace-nowrap flex items-center"
          onClick={() => setAddModelDialog({ isOpen: true, model: null })}
        >
          <PlusCircleIcon className="w-5 h-5 mr-1" />
          Add
        </Button>
      </div>

      <DataTable
        variant="admin_browser"
        apiMethod={GetModels}
        enableSelection={true}
        selectedItems={selectedItems}
        onSelectionChange={(ids) => setSelectedItems(ids)}
        bulkActions={[
          {
            label: "Delete",
            variant: "admin_error",
            icon: <TrashIcon className="w-5 h-5" />,
            onClick: () => handleDeleteSelected(allModels),
          }
        ]}
        itemIdField="_id"
        searchField="name"
        sort={{ created: -1 }}
        localUserPreferencesKey="admin.licensing.models"
        renders={columns}
        refreshKey={refreshKey}
        preferredHeight="60vh"
        itemHeight="h-20"
        onDataUpdate={(data) => setAllModels(data.items || [])}
        noData={
          <div className="text-center py-8">
            <CogIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />
            <Typography
              variant="admin_body"
              className="text-admin_text_secondary"
            >
              No license models found
            </Typography>

            <Typography
              variant="admin_txt1"
              className="text-admin_text_secondary mt-2"
            >
              Models are templates for generating licenses with specific
              patterns and settings
            </Typography>
            
            <div className="flex justify-center mt-4">
              <Button
                variant="admin_primary"
                onClick={() => setAddModelDialog({ isOpen: true, model: null })}
              >
                Create First Model
              </Button>
            </div>
          </div>
        }
        styleOverrides={styleOverrides}
      />

      {/* Add/Edit Model Dialog */}
      <AddModelDialog
        isOpen={addModelDialog.isOpen}
        model={addModelDialog.model}
        onClose={() => setAddModelDialog({ isOpen: false, model: null })}
        onModelSaved={() => {
          setRefreshKey((prev) => prev + 1)
          setAddModelDialog({ isOpen: false, model: null })
        }}
      />
    </div>
  )
}
